/**
 * Token resolver - handles dual token sources (Amplify session + OAuth store)
 * Following NEW_SITE_CONSTITUTION_GUIDE.md Section 4.7
 */
import { fetchAuthSession } from 'aws-amplify/auth';

import { getValidTokenFromProvider } from './authBridge';

const OAUTH_TOKENS_KEY = 'bdance_oauth_tokens';

/**
 * Get access token from Amplify session or OAuth store
 * Use for: Own backend API, BrainDance billing API
 */
export async function getAccessToken(forceRefresh = false): Promise<string | null> {
  // 1. Try Amplify session (direct OTP sign-in) — auto-refreshes via refresh token
  try {
    const session = await fetchAuthSession({ forceRefresh });
    const token = session.tokens?.accessToken?.toString();
    if (token) return token;
  } catch {
    /* fall through */
  }

  // 2. Ask the cognito-passwordless provider for a valid token. Its
  //    getValidToken() refreshes the SSO session when the access token is
  //    near/after expiry — this is what prevents a hard logout mid-session
  //    for SSO users whose stored token has expired.
  const providerToken = await getValidTokenFromProvider();
  if (providerToken) return providerToken;

  // 3. Fall back to the raw OAuth token store (defense in depth)
  try {
    const stored = localStorage.getItem(OAUTH_TOKENS_KEY);
    if (stored) {
      const { accessToken, expiresAt } = JSON.parse(stored);
      if (accessToken && expiresAt > Math.floor(Date.now() / 1000)) {
        return accessToken;
      }
    }
  } catch {
    /* fall through */
  }

  return null;
}

/**
 * Get ID token from Amplify session or OAuth store
 * Use for: Cross-site APIs (PersonaWiz, other family sites)
 * ID token carries email, sub claims
 */
export async function getIdToken(): Promise<string | null> {
  // 1. Try Amplify session
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (token) return token;
  } catch {
    /* fall through */
  }

  // 2. Trigger a provider refresh so the OAuth store's ID token is current.
  //    getValidToken() returns an access token, but its side-effect refreshes
  //    the whole SSO session (access + id) when near/after expiry.
  await getValidTokenFromProvider();

  // 3. Read the (possibly refreshed) ID token from the OAuth store
  try {
    const stored = localStorage.getItem(OAUTH_TOKENS_KEY);
    if (stored) {
      const { idToken, expiresAt } = JSON.parse(stored);
      if (idToken && expiresAt > Math.floor(Date.now() / 1000)) {
        return idToken;
      }
    }
  } catch {
    /* fall through */
  }

  return null;
}

/**
 * Check if we have valid tokens
 */
export async function hasValidSession(): Promise<boolean> {
  const token = await getAccessToken();
  return token !== null;
}

/**
 * Get the user's Cognito sub (user ID)
 * Tries Amplify session first, then OAuth token store
 */
export async function getUserSub(): Promise<string | null> {
  // 1. Try Amplify session
  try {
    const session = await fetchAuthSession();
    const sub = session.tokens?.idToken?.payload?.sub as string;
    if (sub) return sub;
  } catch {
    /* fall through */
  }

  // 2. Fall back to OAuth token store - decode the ID token
  try {
    const stored = localStorage.getItem(OAUTH_TOKENS_KEY);
    if (stored) {
      const { idToken, expiresAt } = JSON.parse(stored);
      if (idToken && expiresAt > Math.floor(Date.now() / 1000)) {
        // Decode JWT payload (base64)
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        if (payload.sub) return payload.sub;
      }
    }
  } catch {
    /* fall through */
  }

  return null;
}
